import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCreditCheckoutSession } from '../services/credits';

export default function CreditsSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('Verifying payment...');
  const sessionId = searchParams.get('session_id');

  const verifyPayment = useCallback(async (sessionId: string) => {
    try {
      setStatus('verifying');
      setMessage('Verifying payment...');
      
      const data = await getCreditCheckoutSession(sessionId);
      
      if (data.session.payment_status === 'paid') {
        setStatus('success');
        setMessage('Credits added successfully!');
        
        // Refresh credit balance if function is available
        if (typeof window !== 'undefined' && (window as any).refreshCreditBalance) {
          (window as any).refreshCreditBalance();
        }
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 2000);
      } else {
        setStatus('error');
        setMessage('Payment verification failed. Please contact support if you were charged.');
      }
    } catch (error) {
      console.error('Failed to verify payment:', error);
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Failed to verify payment');
    }
  }, [navigate]);

  useEffect(() => {
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setStatus('error');
      setMessage('No session ID provided');
    }
  }, [sessionId, verifyPayment]);

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="text-center max-w-md w-full">
        {status === 'verifying' && (
          <>
            <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Verifying Payment</h1>
            <p className="text-white/70">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-white/70 mb-4">{message}</p>
            <p className="text-white/50 text-sm">Redirecting to dashboard...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Verification Failed</h1>
            <p className="text-white/70 mb-6">{message}</p>
            <button
              onClick={() => navigate('/credits')}
              className="bg-white text-black px-6 py-2 rounded-full font-semibold hover:bg-gray-200 transition-colors"
            >
              Back to Credits
            </button>
          </>
        )}
      </div>
    </div>
  );
}

